import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import LinkText from 'ext-ui/components/LinkText/LinkText'
import Stepper, { StepperStep } from 'ext-ui/components/Stepper/Stepper'
import React, { ReactElement, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './GoogleWorkspaceIntegrationSetupModal.style'

export type GoogleWorkspaceIntegrationSetup = {
  clientId: string
  oauthScope: string
  googleAdminConsoleUrl: string
}

type CopyTarget = 'client_id' | 'scope'

type GoogleWorkspaceIntegrationSetupModalProps = {
  isOpen: boolean
  onClose: () => void
  integrationName: string
  setup: GoogleWorkspaceIntegrationSetup
  prerequisiteDescription: string
  prerequisiteItems: string[]
  verificationDescription: string
  successDescription: string
  verifyErrorMessage?: string
  verifyPending: boolean
  connected: boolean
  onVerify: () => void
  onCopy?: () => void
}

export default function GoogleWorkspaceIntegrationSetupModal({
  isOpen,
  onClose,
  integrationName,
  setup,
  prerequisiteDescription,
  prerequisiteItems,
  verificationDescription,
  successDescription,
  verifyErrorMessage,
  verifyPending,
  connected,
  onVerify,
  onCopy,
}: GoogleWorkspaceIntegrationSetupModalProps): ReactElement {
  const translate = useTranslate()
  const [currentStep, setCurrentStep] = useState(1)
  const [copyTarget, setCopyTarget] = useState<CopyTarget | null>(null)
  const [copyError, setCopyError] = useState(false)
  const activeStep = connected ? 3 : currentStep

  const steps: StepperStep[] = [
    { title: translate('Prerequisites') },
    { title: translate('Set up Google Admin') },
    { title: translate('Verify') },
  ]

  const handleCopy = async (target: CopyTarget, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopyTarget(target)
      setCopyError(false)
      onCopy?.()
    } catch {
      setCopyTarget(null)
      setCopyError(true)
    }
  }

  const handleClose = () => {
    if (verifyPending) return
    setCurrentStep(1)
    setCopyTarget(null)
    setCopyError(false)
    onClose()
  }

  const renderStepContent = () => {
    if (activeStep === 1) {
      return (
        <>
          <h2>{translate('Before you begin')}</h2>
          <p>{prerequisiteDescription}</p>
          <ul>
            {prerequisiteItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      )
    }

    if (activeStep === 2) {
      return (
        <>
          <h2>{translate('Set up Google Admin')}</h2>
          <p>
            {translate(
              'Open Domain-wide Delegation in Google Admin. If this Client ID is already listed, edit it and add the OAuth scope below to its existing scopes. Otherwise, select Add new and paste both values.',
            )}
          </p>
          <LinkText
            href={setup.googleAdminConsoleUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate('Open Google Admin console')}
          </LinkText>
          {copyError && (
            <div role="alert">
              <AlertPanel variant="severe-wcag">
                {translate(
                  'Could not copy to the clipboard. Select the value and copy it manually.',
                )}
              </AlertPanel>
            </div>
          )}
          <div className="GoogleWorkspaceIntegrationSetupModal-field">
            <strong>{translate('Client ID')}</strong>
            <div className="GoogleWorkspaceIntegrationSetupModal-code">
              <code>{setup.clientId}</code>
              <Button
                variant="secondary"
                onClick={() => handleCopy('client_id', setup.clientId)}
              >
                {translate('Copy client ID')}
              </Button>
            </div>
            <span aria-live="polite">
              {copyTarget === 'client_id'
                ? translate('Client ID copied.')
                : null}
            </span>
          </div>
          <div className="GoogleWorkspaceIntegrationSetupModal-field">
            <strong>{translate('OAuth scope')}</strong>
            <div className="GoogleWorkspaceIntegrationSetupModal-code GoogleWorkspaceIntegrationSetupModal-code--scope">
              <code>{setup.oauthScope}</code>
              <Button
                variant="secondary"
                onClick={() => handleCopy('scope', setup.oauthScope)}
              >
                {translate('Copy scope')}
              </Button>
            </div>
            <span aria-live="polite">
              {copyTarget === 'scope' ? translate('Scope copied.') : null}
            </span>
          </div>
        </>
      )
    }

    return (
      <>
        <h2>{translate('Verify the connection')}</h2>
        {connected ? (
          <div
            className="GoogleWorkspaceIntegrationSetupModal-success"
            role="status"
          >
            <AlertPanel variant="default-wcag">
              {translate('${integrationName} is connected', {
                integrationName,
              })}
            </AlertPanel>
            <p>{successDescription}</p>
          </div>
        ) : (
          <>
            <p>{verificationDescription}</p>
            {verifyErrorMessage && (
              <div role="alert">
                <AlertPanel variant="severe-wcag">
                  {verifyErrorMessage}
                </AlertPanel>
              </div>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <BaseModal
      className="GoogleWorkspaceIntegrationSetupModal"
      variant="interactive"
      isOpen={isOpen}
      onClose={handleClose}
      shouldCloseOnEsc={!verifyPending}
      shouldCloseOnOverlayClick={!verifyPending}
      css={style}
      aria-label={translate('Set up ${integrationName}', { integrationName })}
    >
      <h1>{translate('Set up ${integrationName}', { integrationName })}</h1>
      <div className="GoogleWorkspaceIntegrationSetupModal-layout">
        <Stepper
          steps={steps}
          currentStep={activeStep}
          onStepSelect={verifyPending || connected ? undefined : setCurrentStep}
          ariaLabel={translate('${integrationName} setup progress', {
            integrationName,
          })}
          getStepNavLabel={(title) =>
            translate('Go back to ${stepTitle}', { stepTitle: title })
          }
        />
        <section className="GoogleWorkspaceIntegrationSetupModal-content">
          {renderStepContent()}
        </section>
      </div>
      <div className="GoogleWorkspaceIntegrationSetupModal-actions">
        {activeStep > 1 && !connected && (
          <Button
            variant="secondary"
            disabled={verifyPending}
            onClick={() => setCurrentStep((step) => step - 1)}
          >
            {translate('Back')}
          </Button>
        )}
        {activeStep < 3 && (
          <Button onClick={() => setCurrentStep((step) => step + 1)}>
            {translate('Continue')}
          </Button>
        )}
        {activeStep === 3 && !connected && (
          <Button
            loading={verifyPending}
            disabled={verifyPending}
            aria-busy={verifyPending}
            onClick={onVerify}
          >
            {translate('Verify connection')}
          </Button>
        )}
        {connected && (
          <Button onClick={handleClose}>{translate('Done')}</Button>
        )}
      </div>
    </BaseModal>
  )
}
